import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box, Drawer, AppBar, Toolbar, List, Typography, IconButton,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar,
  Stack, Divider, Tooltip, Menu, MenuItem, Chip,
  InputBase, useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  FitnessCenter as GymIcon,
  SportsGymnastics as ExerciseIcon,
  Notifications as NotifIcon,
  Groups as BatchIcon,
  Logout as LogoutIcon,
  CalendarMonth as PlanIcon,
  EmojiEvents as ChallengesIcon,
  Restaurant as MealIcon,
  AdminPanelSettings as StaffIcon,
  QrCode2 as AttendanceIcon,
  Storage as StorageIcon,
  Feedback as FeedbackIcon,
  SetMeal as FoodIcon,
  CreditCard as PaymentsIcon,
  AccountBalanceWallet as UpiIcon,
  Schedule as SlotIcon,
  QuestionAnswer as TrainerIcon,
  Psychology as StressIcon,
  SelfImprovement as YogaIcon,
  Settings as SettingsIcon,
  TrendingDown as TrendingDownIcon,
  KeyboardArrowDown,
  ChevronRight,
  Search as SearchIcon,
  Close as CloseIcon,
  HelpOutline as HelpIcon,
  Download as DownloadIcon,
  PersonAddAlt1 as LeadsIcon,
  Message, ViewCarousel, Cable as IntegrationsIcon, PointOfSale as POSIcon,
  Share as SocialIcon, Fingerprint as FingerprintIcon
} from '@mui/icons-material';

import { useAppData } from '../context/AppDataContext';
import { ColorModeContext } from '../context/ThemeContext';
import { GYM_MODULES, getId, canAccessGymRoute } from '../utils/gymModules';
import RequireGymRoute from './RequireGymRoute';
import { BRAND, BLUE_GRADIENT, HERO_GRADIENT, SIDEBAR_GRADIENT } from '../theme/fitisticTokens';

const DRAWER_W = 268;
const DRAWER_COLLAPSED_W = 72;


// ── Role badge ────────────────────────────────────────────────────────────────
const ROLES = {
  superadmin: { label: 'Super Admin', color: '#7C3AED', bg: '#F5F3FF' },
  gym_admin:  { label: 'Gym Admin',   color: '#0891B2', bg: '#ECFEFF' },
  admin:      { label: 'Admin',       color: '#059669', bg: '#ECFDF5' },
  trainer:    { label: 'Trainer',     color: '#D97706', bg: '#FFFBEB' },
  manager:    { label: 'Manager',     color: '#0F766E', bg: '#ECFDF5' },
  coach:      { label: 'Coach',       color: '#1D4ED8', bg: '#EFF6FF' },
  sales_staff:{ label: 'Sales',       color: '#C2410C', bg: '#FFEDD5' },
  front_desk: { label: 'Front Desk',  color: '#0EA5E9', bg: '#EFF6FF' },
};

// ── Nav items ─────────────────────────────────────────────────────────────────
const superadminItems = [
  { divider: true, label: 'Most Used' },
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Communication', icon: <Message />,       path: '/dashboard/marketing' },
  
  { divider: true, label: 'Manage' },
  { text: 'Gyms',          icon: <GymIcon />,       path: '/dashboard/gyms' },
  { text: 'Staff',         icon: <StaffIcon />,     path: '/dashboard/staff' },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { text: 'Banners',       icon: <ViewCarousel />,  path: '/dashboard/banners' },
  
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers' },
  { text: 'Yoga Content',  icon: <YogaIcon />,      path: '/dashboard/yoga-content' },
  { text: 'Stress Content', icon: <StressIcon />,    path: '/dashboard/stress-content' },
  
  { divider: true, label: 'Operations' },
  { text: 'Social Operations', icon: <SocialIcon />, path: '/dashboard/social-operations' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management' },
  { text: 'Challenges & Events', icon: <ChallengesIcon />, path: '/dashboard/challenges-events' },
  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention' },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },

  
  { divider: true, label: 'Payments & POS' },
  { text: 'Payments',      icon: <PaymentsIcon />,  path: '/dashboard/payments' },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Gym Invoices',  icon: <UpiIcon />,       path: '/dashboard/gym-invoices' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices' },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings' },
  
  { divider: true, label: 'Settings & Data' },
  { text: 'Notification Templates', icon: <NotifIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notification-templates' },
  { text: 'Integrations',  icon: <IntegrationsIcon />, path: '/dashboard/integrations' },
  { text: 'Database',      icon: <StorageIcon />,   path: '/dashboard/database' },
];

const gymAdminItems = (gymId) => [
  { divider: true, label: 'Most Used' },
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Communication', icon: <Message />,       path: '/dashboard/marketing' },
  
  { divider: true, label: 'Manage' },
  { text: 'My Gym',        icon: <GymIcon />,       path: `/dashboard/gyms/${gymId}` },
  { text: 'Staff',         icon: <StaffIcon />,     path: '/dashboard/staff', module: GYM_MODULES.trainerStaff },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { text: 'Banners',       icon: <ViewCarousel />,  path: '/dashboard/banners' },
  
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers', module: GYM_MODULES.trainerStaff },
  { text: 'Yoga Content',  icon: <YogaIcon />,      path: '/dashboard/yoga-content', defaultGymOnly: true },
  { text: 'Stress Content', icon: <StressIcon />,    path: '/dashboard/stress-content', defaultGymOnly: true },
  
  { divider: true, label: 'Operations' },
  { text: 'Social Operations', icon: <SocialIcon />, path: '/dashboard/social-operations' },
  { text: 'Biometric & Hardware', icon: <FingerprintIcon />, path: '/dashboard/biometric' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management', module: GYM_MODULES.slotBooking },
  { text: 'Challenges & Events', icon: <ChallengesIcon />, path: '/dashboard/challenges-events' },
  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention', module: GYM_MODULES.memberRetention },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },
  
  { divider: true, label: 'Payments & POS' },
  { text: 'UPI Payments',  icon: <UpiIcon />,       path: '/dashboard/gym-upi-payments', module: GYM_MODULES.invoice },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices', module: GYM_MODULES.invoice },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings', module: GYM_MODULES.invoice },
  
  { divider: true, label: 'Settings' },
  { text: 'Gym Settings',  icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/gym-settings' },
  { text: 'Notification Templates', icon: <NotifIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notification-templates' },
  { text: 'Integrations',  icon: <IntegrationsIcon />, path: '/dashboard/integrations' },
];

const adminItems = [
  { divider: true, label: 'Most Used' },
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Communication', icon: <Message />,       path: '/dashboard/marketing' },
  
  { divider: true, label: 'Manage' },
  { text: 'Staff',         icon: <StaffIcon />,     path: '/dashboard/staff', module: GYM_MODULES.trainerStaff },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { text: 'Banners',       icon: <ViewCarousel />,  path: '/dashboard/banners' },
  
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers', module: GYM_MODULES.trainerStaff },
  
  { divider: true, label: 'Operations' },
  { text: 'Social Operations', icon: <SocialIcon />, path: '/dashboard/social-operations' },
  { text: 'Biometric & Hardware', icon: <FingerprintIcon />, path: '/dashboard/biometric' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management', module: GYM_MODULES.slotBooking },
  { text: 'Challenges & Events', icon: <ChallengesIcon />, path: '/dashboard/challenges-events' },

  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention', module: GYM_MODULES.memberRetention },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },
  
  { divider: true, label: 'Payments & POS' },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices', module: GYM_MODULES.invoice },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings', module: GYM_MODULES.invoice },
  
  { divider: true, label: 'Settings' },
  { text: 'Gym Settings',  icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/gym-settings' },
  { text: 'Notification Templates', icon: <NotifIcon sx={{ fontSize: 18 }} />, path: '/dashboard/notification-templates' },
  { text: 'Integrations',  icon: <IntegrationsIcon />, path: '/dashboard/integrations' },
];

const managerItems = adminItems.filter(item => item.text !== 'Challenges & Events');

const coachItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { text: 'Batches',       icon: <BatchIcon />,     path: '/dashboard/batches' },
  { divider: true, label: 'Content' },
  { text: 'Workout Plans', icon: <PlanIcon />,      path: '/dashboard/workout-plans' },
  { text: 'Meal Plans',    icon: <MealIcon />,      path: '/dashboard/meal-plans' },
  { text: 'Food Items',    icon: <FoodIcon />,      path: '/dashboard/food-items' },
  { text: 'Exercises',     icon: <ExerciseIcon />,  path: '/dashboard/exercises' },
  { text: 'Trainers',      icon: <TrainerIcon />,   path: '/dashboard/trainers', module: GYM_MODULES.trainerStaff },
];

const salesStaffItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { divider: true, label: 'Communication' },
  { text: 'Communication',     icon: <Message />,       path: '/dashboard/marketing' },
  { divider: true, label: 'Operations' },
  { text: 'Leads',         icon: <LeadsIcon />,     path: '/dashboard/leads' },
  { text: 'Retention',     icon: <TrendingDownIcon />, path: '/dashboard/retention', module: GYM_MODULES.memberRetention },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Invoices',      icon: <UpiIcon />,       path: '/dashboard/invoices', module: GYM_MODULES.invoice },
  { text: 'Invoice Settings', icon: <SettingsIcon sx={{ fontSize: 18 }} />, path: '/dashboard/invoice-settings', module: GYM_MODULES.invoice },
  { text: 'Feedback',      icon: <FeedbackIcon />,  path: '/dashboard/feedback' },
];

const frontDeskItems = [
  { text: 'Dashboard',     icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'Users',         icon: <PeopleIcon />,    path: '/dashboard/users' },
  { divider: true, label: 'Operations' },
  { text: 'POS',           icon: <POSIcon />,       path: '/dashboard/pos' },
  { text: 'Attendance',    icon: <AttendanceIcon />, path: '/dashboard/attendance' },
  { text: 'Slot Management', icon: <SlotIcon />,    path: '/dashboard/slot-management', module: GYM_MODULES.slotBooking },
];

function cleanDividers(items) {
  return items.filter((item, index, arr) => {
    if (!item.divider) return true;
    const next = arr[index + 1];
    return next && !next.divider;
  });
}

function filterModuleItems(items, user, gyms) {
  return cleanDividers(items.filter(item => {
    if (item.divider) return true;
    if (item.defaultGymOnly) {
      const defaultGym = gyms.find(g => g.isDefault);
      if (user?.role !== 'admin' || !defaultGym || getId(user.gymId) !== getId(defaultGym)) return false;
    }
    return canAccessGymRoute(user, gyms, item.path);
  }));
}

function getMenuItems(user, gyms) {
  if (!user) return [];
  if (user.role === 'superadmin') return superadminItems;
  if (user.role === 'gym_admin') return filterModuleItems(gymAdminItems(getId(user.gymId)), user, gyms);
  if (user.role === 'manager') return filterModuleItems(managerItems, user, gyms);
  if (user.role === 'coach') return filterModuleItems(coachItems, user, gyms);
  if (user.role === 'sales_staff') return filterModuleItems(salesStaffItems, user, gyms);
  if (user.role === 'front_desk') return filterModuleItems(frontDeskItems, user, gyms);
  return filterModuleItems(adminItems, user, gyms);
}

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

function initials(name) {
  if (!name) return '?';
  const p = name.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name[0].toUpperCase();
}

function isActive(item, pathname) {
  if (item.path === '/dashboard') return pathname === '/dashboard';
  return pathname.startsWith(item.path);
}

// ── Helpers for the header breadcrumb ─────────────────────────────────────────
function findActive(items, pathname) {
  let section = null;
  let best = null;
  let bestSection = null;
  items.forEach(item => {
    if (item.divider) { section = item.label; return; }
    if (isActive(item, pathname) && (!best || item.path.length > best.path.length)) {
      best = item;
      bestSection = section;
    }
  });
  return { item: best, section: bestSection };
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── Sidebar content (White with Black & Purple theme, collapsible) ─────────────
function SidebarContent({ items, location, navigate, onClose, user, role, onSignOut, searchRef, collapsed, onExpand }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const visible = useMemo(() => {
    if (!q) return items;
    return cleanDividers(items.filter(item => item.divider || item.text.toLowerCase().includes(q)));
  }, [items, q]);

  const firstMatch = visible.find(i => !i.divider);

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#FFFFFF',
      color: '#0F172A',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Brand */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={collapsed ? 'center' : 'flex-start'}
        spacing={collapsed ? 0 : 1.25}
        sx={{
          px: collapsed ? 1 : 2.25,
          height: 68,
          flexShrink: 0,
          position: 'relative',
          cursor: 'pointer',
          borderBottom: '1px solid #E2E8F0',
        }}
        onClick={() => { navigate('/dashboard'); onClose?.(); }}
      >
        <Tooltip title={collapsed ? 'Fitistic Dashboard' : ''} placement="right" disableHoverListener={!collapsed}>
          <Box sx={{
            width: 40, height: 40,
            borderRadius: '12px',
            bgcolor: '#FFFFFF',
            border: '1.5px solid #E2E8F0',
            display: 'grid', placeItems: 'center',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(15,23,42,0.06)',
            p: 0.5,
            flexShrink: 0,
            transition: 'transform 0.15s ease',
            '&:hover': { transform: 'scale(1.05)' },
          }}>
            <img
              src="/logo.png"
              alt="Fitistic"
              style={{ width: 28, height: 28, objectFit: 'contain', display: 'block' }}
              onError={e => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
              }}
            />
            <Box sx={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', bgcolor: '#F5F3FF' }}>
              <Typography sx={{ color: '#7C3AED', fontWeight: 800, fontSize: 16 }}>F</Typography>
            </Box>
          </Box>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.125rem', color: '#0F172A', letterSpacing: '-0.01em', lineHeight: 1.1 }}>Fitistic</Typography>
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#7C3AED', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Admin Portal</Typography>
          </Box>
        )}
      </Stack>

      {/* Search menu */}
      <Box sx={{ px: collapsed ? 1 : 1.75, py: 1.5, position: 'relative' }}>
        {collapsed ? (
          <Tooltip title="Find a page (Ctrl + K)" placement="right">
            <IconButton
              size="small"
              onClick={onExpand}
              aria-label="Expand and search"
              sx={{
                width: 40,
                height: 38,
                borderRadius: '10px',
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                color: '#64748B',
                mx: 'auto',
                display: 'flex',
                '&:hover': { bgcolor: '#FFFFFF', borderColor: '#7C3AED', color: '#7C3AED' },
              }}
            >
              <SearchIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.25, height: 38, borderRadius: '10px',
            bgcolor: '#F8FAFC', border: '1px solid #E2E8F0',
            transition: 'all 0.15s',
            '&:focus-within': { borderColor: '#7C3AED', bgcolor: '#FFFFFF', boxShadow: '0 0 0 3px rgba(124,58,237,0.12)' },
          }}>
            <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
            <InputBase
              inputRef={searchRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && firstMatch) { navigate(firstMatch.path); setQuery(''); onClose?.(); }
                if (e.key === 'Escape') { setQuery(''); e.currentTarget.blur(); }
              }}
              placeholder="Find a page…"
              inputProps={{ 'aria-label': 'Search menu', id: 'sidebar-search' }}
              sx={{ flex: 1, color: '#0F172A', fontSize: '0.8125rem', '& input::placeholder': { color: '#94A3B8', opacity: 1 } }}
            />
            {query ? (
              <IconButton size="small" onClick={() => setQuery('')} aria-label="Clear search" sx={{ p: 0.25, color: '#94A3B8', '&:hover': { color: '#0F172A' } }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            ) : (
              <Box sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#94A3B8', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', borderRadius: '6px', px: 0.75, py: 0.125, display: { xs: 'none', sm: 'block' } }}>
                Ctrl K
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Nav */}
      <List sx={{
        px: collapsed ? 1 : 1.25, pt: 0, pb: 2, flexGrow: 1, overflowY: 'auto', position: 'relative',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#E2E8F0', borderColor: 'transparent', borderRadius: 4 },
      }}>
        {visible.length === 0 && !collapsed && (
          <Typography sx={{ px: 1.5, py: 3, fontSize: '0.8125rem', color: '#94A3B8', textAlign: 'center' }}>
            No pages match “{query}”
          </Typography>
        )}
        {visible.map((item, idx) => {
          if (item.divider) {
            if (collapsed) {
              return <Divider key={`d-${idx}`} sx={{ my: 1, mx: 1, borderColor: '#F1F5F9' }} />;
            }
            return (
              <Typography key={`d-${idx}`} sx={{ px: 1.5, pt: idx === 0 ? 0.5 : 2, pb: 0.75, fontSize: '0.6875rem', fontWeight: 700, color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                {item.label}
              </Typography>
            );
          }
          const active = isActive(item, location.pathname);
          const button = (
            <ListItemButton
              id={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
              onClick={() => { navigate(item.path); setQuery(''); onClose?.(); }}
              aria-current={active ? 'page' : undefined}
              sx={{
                borderRadius: '10px',
                py: 0.875,
                px: collapsed ? 0 : 1.5,
                minHeight: 40,
                justifyContent: collapsed ? 'center' : 'flex-start',
                color: active ? '#7C3AED' : '#334155',
                bgcolor: active ? '#F5F3FF' : 'transparent',
                border: `1px solid ${active ? '#DDD6FE' : 'transparent'}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: active ? '#EDE9FE' : '#F8FAFC',
                  color: active ? '#7C3AED' : '#0F172A',
                  transform: active ? 'none' : 'translateX(2px)',
                },
                '&:hover .nav-icon': { color: active ? '#7C3AED' : '#0F172A' },
                '&:focus-visible': { outline: '2px solid #7C3AED', outlineOffset: 1 },
              }}
            >
              <ListItemIcon className="nav-icon" sx={{
                minWidth: collapsed ? 0 : 34,
                justifyContent: 'center',
                color: active ? '#7C3AED' : '#64748B',
                transition: 'color 0.15s',
              }}>
                {React.cloneElement(item.icon, { sx: { fontSize: 19 } })}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: active ? 700 : 500, color: 'inherit', noWrap: true }}
                />
              )}
            </ListItemButton>
          );

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.375 }}>
              {collapsed ? (
                <Tooltip title={item.text} placement="right" arrow>
                  {button}
                </Tooltip>
              ) : button}
            </ListItem>
          );
        })}
      </List>

      {/* User card */}
      {user && (
        <Box sx={{
          p: collapsed ? 1 : 1.5,
          borderTop: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          position: 'relative',
        }}>
          {collapsed ? (
            <Tooltip title={`${user.name} (${role.label}) · Click to sign out`} placement="right" arrow>
              <IconButton
                onClick={onSignOut}
                aria-label="Sign out"
                sx={{
                  width: 42,
                  height: 42,
                  mx: 'auto',
                  display: 'flex',
                  borderRadius: '12px',
                  p: 0.5,
                  '&:hover': { bgcolor: 'rgba(239,68,68,0.08)' },
                }}
              >
                <Avatar sx={{ width: 34, height: 34, bgcolor: '#7C3AED', color: '#FFFFFF', fontSize: '0.8125rem', fontWeight: 700 }}>
                  {initials(user.name)}
                </Avatar>
              </IconButton>
            </Tooltip>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ p: 1, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
              <Avatar sx={{ width: 34, height: 34, bgcolor: '#7C3AED', color: '#FFFFFF', fontSize: '0.8125rem', fontWeight: 700 }}>
                {initials(user.name)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700, color: '#0F172A', lineHeight: 1.2 }}>{user.name}</Typography>
                <Typography noWrap sx={{ fontSize: '0.6875rem', color: '#64748B' }}>{role.label}{user.gymName ? ` · ${user.gymName}` : ''}</Typography>
              </Box>
              <Tooltip title="Sign out">
                <IconButton size="small" onClick={onSignOut} aria-label="Sign out" sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.08)' } }}>
                  <LogoutIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Main layout ───────────────────────────────────────────────────────────────
export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed]   = useState(() => {
    try {
      return localStorage.getItem('fitistic_sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [anchorEl, setAnchorEl]     = useState(null);
  const { gyms } = useAppData();
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = getUser();
  const items     = getMenuItems(user, gyms);
  const role      = ROLES[user?.role] || ROLES.admin;
  const searchRef = useRef(null);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  // eslint-disable-next-line no-unused-vars
  const colorMode = useContext(ColorModeContext);

  const currentDrawerWidth = collapsed ? DRAWER_COLLAPSED_W : DRAWER_W;

  const toggleSidebar = () => {
    if (isDesktop) {
      setCollapsed(prev => {
        const next = !prev;
        try { localStorage.setItem('fitistic_sidebar_collapsed', String(next)); } catch {}
        return next;
      });
    } else {
      setMobileOpen(prev => !prev);
    }
  };

  const { item: activeItem, section: activeSection } = findActive(items, location.pathname);
  const isHome = location.pathname === '/dashboard';

  // Keep the browser tab title in sync with the current page
  useEffect(() => {
    document.title = activeItem ? `${activeItem.text} · Fitistic Admin` : 'Fitistic Admin';
  }, [activeItem]);

  // Ctrl/Cmd + K → focus menu search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!isDesktop) {
          setMobileOpen(true);
        } else if (collapsed) {
          setCollapsed(false);
          try { localStorage.setItem('fitistic_sidebar_collapsed', 'false'); } catch {}
        }
        setTimeout(() => searchRef.current?.focus(), isDesktop ? 150 : 250);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDesktop, collapsed]);

  const signOut = () => {
    setAnchorEl(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const T = {
    primary: theme.palette.primary.main,
    primaryDark: theme.palette.primary.dark,
    bg: theme.palette.background.default,
    surface: theme.palette.background.paper,
    surfaceAlt: BRAND.surfaceSoft,
    border: theme.palette.divider,
    textDark: theme.palette.text.primary,
    textMid: theme.palette.text.secondary,
    textLight: theme.palette.text.disabled,
    red: theme.palette.error.main,
  };

  const iconBtnSx = { width: 38, height: 38, borderRadius: '10px', border: `1px solid ${T.border}`, color: T.textMid, bgcolor: T.surface };

  const sidebar = (onClose, isCollapsed = false) => (
    <SidebarContent
      items={items}
      location={location}
      navigate={navigate}
      onClose={onClose}
      user={user}
      role={role}
      onSignOut={signOut}
      searchRef={searchRef}
      collapsed={isCollapsed}
      onExpand={() => {
        setCollapsed(false);
        try { localStorage.setItem('fitistic_sidebar_collapsed', 'false'); } catch {}
        setTimeout(() => searchRef.current?.focus(), 150);
      }}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: T.bg }}>

      {/* ── Top AppBar ── */}
      <AppBar position="fixed" elevation={0} sx={{
        width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
        ml: { sm: `${currentDrawerWidth}px` },
        bgcolor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'saturate(180%) blur(12px)',
        borderBottom: `1px solid ${T.border}`,
        transition: theme.transitions.create(['width', 'margin-left'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.shorter,
        }),
      }}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '68px !important', px: { xs: 1.5, sm: 2.5, lg: 3 }, gap: 1.5 }}>

          {/* Left: hamburger (desktop & mobile collapsible toggle) + breadcrumb */}
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
            <Tooltip title={isDesktop ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : 'Toggle menu'}>
              <IconButton
                size="small"
                onClick={toggleSidebar}
                aria-label={isDesktop ? (collapsed ? 'Expand sidebar' : 'Collapse sidebar') : 'Toggle menu'}
                sx={{
                  ...iconBtnSx,
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: '#F5F3FF', borderColor: '#DDD6FE', color: '#7C3AED' },
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box minWidth={0}>
              {isHome ? (
                <>
                  <Typography noWrap sx={{ fontSize: '0.75rem', color: T.textMid, fontWeight: 500 }}>
                    {greeting()},
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '1.0625rem', fontWeight: 800, color: T.textDark, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                    {user?.name?.split(' ')[0] || 'there'} 👋
                  </Typography>
                </>
              ) : (
                <>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ fontSize: '0.75rem', color: T.textMid }}>
                    <Box component="span" onClick={() => navigate('/dashboard')} sx={{ cursor: 'pointer', fontWeight: 500, '&:hover': { color: T.primary } }}>Home</Box>
                    {activeSection && (<><ChevronRight sx={{ fontSize: 14, color: T.textLight }} /><Box component="span" sx={{ fontWeight: 500 }}>{activeSection}</Box></>)}
                  </Stack>
                  <Typography noWrap sx={{ fontSize: '1.0625rem', fontWeight: 800, color: T.textDark, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                    {activeItem?.text || 'Fitistic Admin'}
                  </Typography>
                </>
              )}
            </Box>
          </Stack>

          {/* Right: user pill */}
          <Stack direction="row" spacing={{ xs: 0.75, sm: 1 }} alignItems="center">
            {/* User pill */}
            {user && (
              <>
                <Tooltip title="Account options">
                  <Box
                    component="button"
                    type="button"
                    aria-label="Account options"
                    onClick={e => setAnchorEl(e.currentTarget)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      pl: 0.5, pr: { xs: 0.5, sm: 1 }, py: 0.5, borderRadius: '12px',
                      border: `1px solid ${T.border}`,
                      cursor: 'pointer',
                      bgcolor: T.surface,
                      font: 'inherit',
                      transition: 'all 0.15s',
                      minHeight: 38,
                      '&:hover': { bgcolor: T.surfaceAlt },
                      '&:focus-visible': { outline: `2px solid ${T.primary}`, outlineOffset: 2 },
                    }}
                  >
                    <Avatar sx={{ width: 30, height: 30, background: BLUE_GRADIENT, fontSize: '0.75rem' }}>
                      {initials(user.name)}
                    </Avatar>
                    <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left' }}>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: T.textDark, lineHeight: 1.2 }}>
                        {user.name?.split(' ')[0]}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6875rem', color: T.textMid, lineHeight: 1.2 }}>
                        {role.label}
                      </Typography>
                    </Box>
                    <KeyboardArrowDown sx={{ fontSize: 18, color: T.textLight, display: { xs: 'none', md: 'block' } }} />
                  </Box>
                </Tooltip>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{ sx: { mt: 1, minWidth: 260, p: 0, overflow: 'hidden' } }}
                  MenuListProps={{ sx: { p: 0.5 } }}
                >
                  <Box sx={{ px: 2, py: 2, m: -0.5, mb: 0.5, bgcolor: '#F8FAFC', borderBottom: `1px solid ${T.border}` }}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ width: 42, height: 42, bgcolor: '#7C3AED', color: '#FFFFFF', fontSize: '0.9375rem', fontWeight: 700 }}>
                        {initials(user.name)}
                      </Avatar>
                      <Box minWidth={0}>
                        <Typography noWrap sx={{ fontWeight: 700, color: T.textDark, fontSize: '0.9375rem' }}>
                          {user.name}
                        </Typography>
                        <Typography noWrap sx={{ color: T.textMid, fontSize: '0.75rem' }}>
                          {user.email}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.25 }}>
                      <Chip label={role.label} size="small"
                        sx={{ bgcolor: '#F5F3FF', color: '#7C3AED', border: '1px solid #DDD6FE', fontWeight: 700 }} />
                      {user.gymName && (
                        <Typography noWrap sx={{ fontSize: '0.75rem', color: T.textMid }}>
                          {user.gymName}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <MenuItem
                    component="a"
                    href="/fitistic-staff.apk"
                    download
                    onClick={() => setAnchorEl(null)}
                    sx={{ fontWeight: 600, py: 1.25 }}
                  >
                    <DownloadIcon fontSize="small" sx={{ mr: 1.5, color: T.primary }} />
                    Download Staff App
                  </MenuItem>
                  <MenuItem onClick={() => { setAnchorEl(null); window.open('/admin-guide', '_blank', 'noopener'); }} sx={{ fontWeight: 600, py: 1.25 }}>
                    <HelpIcon fontSize="small" sx={{ mr: 1.5, color: T.textMid }} />
                    Admin Guide
                  </MenuItem>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem onClick={signOut} sx={{ color: T.red, fontWeight: 600, py: 1.25 }}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                    Sign out
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ── Sidebar ── */}
      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        {/* Mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: DRAWER_W, borderRight: '1px solid #E2E8F0', bgcolor: '#FFFFFF' },
          }}
        >
          {sidebar(() => setMobileOpen(false), false)}
        </Drawer>
        {/* Desktop */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: currentDrawerWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            '& .MuiDrawer-paper': {
              width: currentDrawerWidth,
              overflowX: 'hidden',
              borderRight: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
          open
        >
          {sidebar(undefined, collapsed)}
        </Drawer>
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        role="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: T.bg,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
          transition: theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: '68px !important', flexShrink: 0 }} />
        <Box key={location.pathname} sx={{
          flexGrow: 1, p: { xs: 1.5, sm: 2.5, lg: 3 }, maxWidth: 1600, width: '100%', mx: 'auto',
          animation: 'fitPageIn 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes fitPageIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}>
          <RequireGymRoute><Outlet /></RequireGymRoute>
        </Box>
      </Box>
    </Box>
  );
}
